import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/AuthContext";
import { Copy, Check, ArrowLeft, Code2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { LangSwitcher } from "@/components/LangSwitcher";
import { SeoHead } from "@/components/SeoHead";
import { StructuredData, breadcrumbSchema } from "@/components/StructuredData";

const EMBED_SNIPPET = `<div data-depoia="sua-marca"></div>
<script src="https://depoia.com.br/embed.js" async></script>`;

const IFRAME_SNIPPET = `<iframe
  src="https://depoia.com.br/w/sua-marca"
  width="100%" height="640" frameborder="0"
  style="border:none;background:transparent"
  loading="lazy"
></iframe>`;

// Each platform gets a short tag + numbered steps (translation keys under integrations.platforms.<slug>)
const PLATFORMS = [
  { slug: "wordpress",   tag: "cms",       steps: 4 },
  { slug: "webflow",     tag: "builder",   steps: 4 },
  { slug: "shopify",     tag: "ecommerce", steps: 4 },
  { slug: "wix",         tag: "builder",   steps: 4 },
  { slug: "squarespace", tag: "builder",   steps: 4 },
  { slug: "framer",      tag: "builder",   steps: 3 },
  { slug: "notion",      tag: "docs",      steps: 3 },
  { slug: "carrd",       tag: "builder",   steps: 3 },
  { slug: "bubble",      tag: "no-code",   steps: 3 },
  { slug: "html",        tag: "custom",    steps: 2 },
];

function PlatformIcon({ slug }) {
  // Minimal on-brand mono glyphs (no third-party logos to avoid trademark issues)
  const common = "w-8 h-8";
  switch (slug) {
    case "wordpress":
      return <div className={`${common} rounded-full bg-zinc-900 text-white flex items-center justify-center text-[13px] font-black tracking-tighter`}>W</div>;
    case "webflow":
      return <div className={`${common} rounded-md bg-[#146EF5] text-white flex items-center justify-center text-[14px] font-black`}>W</div>;
    case "shopify":
      return <div className={`${common} rounded-md bg-[#95BF47] text-white flex items-center justify-center text-[14px] font-black`}>S</div>;
    case "wix":
      return <div className={`${common} rounded-md bg-[#0C6EFC] text-white flex items-center justify-center text-[13px] font-black italic`}>Wix</div>;
    case "squarespace":
      return <div className={`${common} rounded-md bg-black text-white flex items-center justify-center text-[14px] font-black`}>Sq</div>;
    case "framer":
      return <div className={`${common} rounded-md bg-[#0055FF] text-white flex items-center justify-center text-[14px] font-black`}>F</div>;
    case "notion":
      return <div className={`${common} rounded-md bg-white border border-zinc-300 text-zinc-900 flex items-center justify-center text-[14px] font-black`}>N</div>;
    case "carrd":
      return <div className={`${common} rounded-md bg-[#EF4444] text-white flex items-center justify-center text-[14px] font-black`}>c</div>;
    case "bubble":
      return <div className={`${common} rounded-md bg-[#0F172A] text-white flex items-center justify-center text-[14px] font-black`}>B</div>;
    case "html":
      return <div className={`${common} rounded-md bg-[#FF4F00] text-white flex items-center justify-center`}><Code2 className="w-4 h-4" /></div>;
    default:
      return <div className={`${common} rounded-md bg-zinc-200 flex items-center justify-center`} />;
  }
}

function CopySnippet({ code, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(label || "Copiado!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Não consegui copiar. Selecione o código manualmente.");
    }
  };
  return (
    <div className="relative">
      <pre
        data-testid="integrations-snippet-pre"
        className="bg-zinc-900 text-zinc-100 text-[12px] leading-relaxed rounded-lg p-4 pr-14 overflow-x-auto font-mono"
      >{code}</pre>
      <button
        data-testid="integrations-snippet-copy"
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs px-2.5 py-1.5 rounded-md transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
}

export default function Integrations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selected, setSelected] = useState("wordpress");

  const platform = useMemo(() => PLATFORMS.find((p) => p.slug === selected) || PLATFORMS[0], [selected]);
  const stepsKeys = useMemo(
    () => Array.from({ length: platform.steps }, (_, i) => `integrations.platforms.${platform.slug}.step${i + 1}`),
    [platform]
  );

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <SeoHead pageKey="integrations" />
      <StructuredData data={breadcrumbSchema([
        { name: "depoia", url: `https://depoia.com.br/${(t("integrations.footerLink") === "Integrações" ? "pt" : "en")}` },
        { name: t("integrations.footerLink"), url: `https://depoia.com.br/${(t("integrations.footerLink") === "Integrações" ? "pt" : "en")}/integrations` },
      ])} />
      {/* Top nav */}
      <header className="max-w-6xl mx-auto px-6 pt-8 pb-4 flex items-center justify-between">
        <Link data-testid="integrations-home-link" to="/" className="flex items-center gap-2 text-lg font-black tracking-tight">
          <div className="w-8 h-8 rounded-lg bg-[#FF4F00] flex items-center justify-center text-white font-black">d.</div>
          depoia
        </Link>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          <Link data-testid="integrations-back-link" to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {user ? t("common.panel") : t("common.back")}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-14">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#FF4F00] font-bold mb-4">{t("integrations.kicker")}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] max-w-3xl">
          {t("integrations.title")}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
          {t("integrations.subtitle")}
        </p>
      </section>

      {/* Platform grid */}
      <section className="max-w-6xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {PLATFORMS.map((p) => {
            const active = p.slug === selected;
            return (
              <button
                key={p.slug}
                data-testid={`integrations-platform-${p.slug}`}
                onClick={() => setSelected(p.slug)}
                className={`group flex items-center gap-3 rounded-xl border p-3.5 text-left transition-all ${
                  active
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-sm"
                    : "border-zinc-200 bg-white hover:border-zinc-400 hover:shadow-sm"
                }`}
              >
                <PlatformIcon slug={p.slug} />
                <div className="min-w-0">
                  <div className={`font-semibold text-sm truncate ${active ? "text-white" : "text-zinc-900"}`}>
                    {t(`integrations.platforms.${p.slug}.name`)}
                  </div>
                  <div className={`text-[11px] uppercase tracking-wider ${active ? "text-zinc-400" : "text-zinc-500"}`}>
                    {p.tag}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Selected platform tutorial */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Left: steps */}
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 mb-2">
              <PlatformIcon slug={platform.slug} />
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
                {t(`integrations.platforms.${platform.slug}.name`)}
              </h2>
            </div>
            <p
              data-testid="integrations-platform-intro"
              className="text-zinc-600 text-sm sm:text-base mb-8 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t(`integrations.platforms.${platform.slug}.intro`) }}
            />

            <ol className="space-y-5">
              {stepsKeys.map((k, i) => (
                <li key={k} data-testid={`integrations-step-${i + 1}`} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-sm font-bold text-zinc-900">
                    {i + 1}
                  </div>
                  <div className="pt-1 text-[15px] text-zinc-800 leading-relaxed flex-1"
                    dangerouslySetInnerHTML={{ __html: t(k) }}
                  />
                </li>
              ))}
            </ol>

            {/* Extra help note per platform */}
            <div className="mt-8 rounded-lg bg-zinc-50 border border-zinc-200 p-4 text-sm text-zinc-600 leading-relaxed">
              <strong className="text-zinc-900">{t("integrations.tipLabel")}</strong>{" "}
              <span dangerouslySetInnerHTML={{ __html: t(`integrations.platforms.${platform.slug}.tip`) }} />
            </div>
          </div>

          {/* Right: code snippet + docs */}
          <div className="md:col-span-2">
            <div className="sticky top-6 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">
                  {t("integrations.snippetLabel")}
                </div>
                <CopySnippet code={EMBED_SNIPPET} label={t("integrations.copied")} />
                <p className="text-xs text-zinc-500 mt-2">
                  {t("integrations.replaceHint")}
                </p>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">
                  {t("integrations.iframeLabel")}
                </div>
                <CopySnippet code={IFRAME_SNIPPET} label={t("integrations.copied")} />
                <p className="text-xs text-zinc-500 mt-2">
                  {t("integrations.iframeHint")}
                </p>
              </div>

              <a
                href="mailto:contato@depoia.com.br?subject=Ajuda%20com%20integra%C3%A7%C3%A3o"
                data-testid="integrations-help-link"
                className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 hover:border-zinc-400 p-4 transition-colors bg-white"
              >
                <div>
                  <div className="font-semibold text-sm text-zinc-900">{t("integrations.helpTitle")}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{t("integrations.helpDesc")}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-zinc-900 text-white p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight">{t("integrations.ctaTitle")}</h3>
            <p className="text-zinc-400 mt-2 text-sm sm:text-base">{t("integrations.ctaSub")}</p>
          </div>
          <Link
            data-testid="integrations-cta-start"
            to={user ? "/dashboard" : "/login"}
            className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#e64700] text-white font-semibold px-6 py-3.5 rounded-full text-sm transition-colors"
          >
            {user ? t("common.panel") : t("landing.ctaStart")}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-zinc-200 flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-400">
        <span>{t("landing.footerTag")}</span>
        <div className="flex gap-6">
          <Link data-testid="footer-integrations-link" to="/integrations" className="hover:text-zinc-700 transition-colors">{t("integrations.footerLink")}</Link>
          <Link data-testid="footer-privacy-link" to="/privacidade" className="hover:text-zinc-700 transition-colors">{t("landing.privacy")}</Link>
          <Link data-testid="footer-terms-link" to="/termos" className="hover:text-zinc-700 transition-colors">{t("landing.terms")}</Link>
        </div>
      </footer>
    </div>
  );
}
